import React, { useEffect, useRef, useState } from 'react';
import { BrandedAlertModal } from 'components/Modal/BrandedAlertModal';
import {
    closeChatbaseConversation, dismissChatbaseFallback, failChatbase, getChatbaseSnapshot, markChatbaseAvailable,
    setChatbaseActor, subscribeChatbase, type ChatbaseStatus,
} from './chatbaseLifecycle';

interface ChatbaseUserMetadata {
    name?: string;
    email?: string;
    role?: 'hygienist' | 'dentist' | 'assistant' | 'admin' | 'office' | 'talent' | string;
    membershipTier?: string;
    region?: string;
    [key: string]: unknown;
}

export interface ChatbaseUserContext {
    userId?: string;
    userHash?: string;
    metadata?: ChatbaseUserMetadata;
}

interface ChatbaseWidgetProps {
    disabled?: boolean;
    userContext?: ChatbaseUserContext | null;
    onLoad?: () => void;
    onError?: (error: Error) => void;
    onStatusChange?: (status: ChatbaseStatus) => void;
    onContactSupport?: () => void;
}

declare global {
    interface Window {
        embeddedChatbotConfig?: {
            chatbotId: string;
            domain: string;
        };
        chatbase?: {
            open?: () => void;
            close?: () => void;
            resetChat?: () => void; // Official Chatbase API to reset conversation
            identify?: (userConfig: {
                user_id?: string;
                user_hash?: string;
                user_metadata?: ChatbaseUserMetadata;
            }) => void;
        };
        chatbaseUserConfig?: {
            user_id?: string;
            user_hash?: string;
            user_metadata?: ChatbaseUserMetadata;
        };
    }
}

const CHATBASE_DOMAIN = 'www.chatbase.co';
const CHATBASE_SCRIPT_SRC = `https://${CHATBASE_DOMAIN}/embed.min.js`;
const CHATBASE_OPEN_EVENT = 'chatbase:open';
const CHATBASE_CLOSE_FALLBACK_ID = 'tempstars-chatbase-close-btn';
const CHATBASE_STYLE_ID = 'chatbase-safe-area-styles';
const CHATBASE_UI_HIDDEN_CLASS = 'tempstars-chatbase-ui-hidden';
const TEASER_DISMISS_PATCHED_ATTR = 'data-tempstars-dismiss-patched';

// Chatbase renamed the open-chat container from `chatbase-chat-window` to
// `chatbase-bubble-window`. Keep both so we survive future renames either way.
const CHAT_WINDOW_SELECTORS = [
    '#chatbase-bubble-window',
    '.chatbase-bubble-window',
    '#chatbase-chat-window',
    '.chatbase-chat-window',
];

const TEASER_DISMISS_BUTTON_SELECTORS = [
    '#chatbase-message-bubbles button[aria-label="Close notifications"]',
    '[class*="chatbase-message-bubbles"] button[aria-label="Close notifications"]',
];

const TEASER_DISMISS_SVG =
    '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" aria-hidden="true"><path d="M18 6L6 18M6 6l12 12"/></svg>';

const isMobileChatbase = () =>
    typeof window !== 'undefined' &&
    (!!(window as any).cordova || window.matchMedia('(pointer: coarse)').matches);

const isFeatureEnabled = () => {
    const flag = process.env.REACT_APP_CHATBASE_ENABLED;
    if (typeof flag === 'string') {
        return flag.toLowerCase() === 'true';
    }
    return true;
};

const isChatbaseAvailable = () =>
    typeof window !== 'undefined' &&
    !!window.chatbase &&
    typeof window.chatbase.open === 'function';

/**
 * Resets the Chatbase conversation using the official resetChat() API
 * This clears the current conversation and starts a new session
 */
const resetChatbaseConversation = () => {
    try {
        // Use the official Chatbase resetChat() API
        const chatbase = window.chatbase;
        if (chatbase && typeof chatbase.resetChat === 'function') {
            chatbase.resetChat();
        }
    } catch (error) {
        console.warn('Error resetting Chatbase conversation:', error);
    }
};

const isElementVisible = (el: HTMLElement): boolean => {
    if (!el.isConnected) return false;
    const style = window.getComputedStyle(el);
    if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') {
        return false;
    }
    const rect = el.getBoundingClientRect();
    return rect.width > 80 && rect.height > 80;
};

const findChatWindow = (): HTMLElement | null => {
    for (const selector of CHAT_WINDOW_SELECTORS) {
        const el = document.querySelector<HTMLElement>(selector);
        if (el && isElementVisible(el)) {
            return el;
        }
    }

    const iframe = document.querySelector<HTMLElement>('iframe[src*="chatbase"]');
    if (iframe && isElementVisible(iframe)) {
        return iframe.parentElement ?? iframe;
    }

    return null;
};

const setCloseFallbackVisible = (visible: boolean) => {
    const btn = document.getElementById(CHATBASE_CLOSE_FALLBACK_ID);
    if (!btn) return;
    btn.classList.toggle('tempstars-chatbase-close-btn--visible', visible);
    btn.setAttribute('aria-hidden', visible ? 'false' : 'true');
};

const closeChatbaseWidget = () => {
    closeChatbaseConversation();
    setCloseFallbackVisible(false);
};

let legacyChatbaseUiHidden = false;
const chatbaseUiHiddenOwners = new Set<symbol>();

const syncChatbaseUiVisibility = () => {
    if (typeof document === 'undefined') return;
    const hidden = legacyChatbaseUiHidden || chatbaseUiHiddenOwners.size > 0;
    // Close again before release in case the SDK mounted a window while hidden.
    if (hidden || document.body.classList.contains(CHATBASE_UI_HIDDEN_CLASS)) {
        closeChatbaseWidget();
        dismissChatbaseFallback();
    }
    document.body.classList.toggle(CHATBASE_UI_HIDDEN_CLASS, hidden);
};

/** Keep existing page-level callers compatible with independently mounted modals. */
export const setChatbaseUiHidden = (hidden: boolean) => {
    legacyChatbaseUiHidden = hidden;
    syncChatbaseUiVisibility();
};

/** Release only this caller's suppression, even if another surface is still open. */
export const acquireChatbaseUiHidden = (): (() => void) => {
    if (typeof document === 'undefined') return () => undefined;
    const owner = Symbol('chatbase-ui-hidden');
    chatbaseUiHiddenOwners.add(owner);
    syncChatbaseUiVisibility();
    return () => {
        if (chatbaseUiHiddenOwners.delete(owner)) syncChatbaseUiVisibility();
    };
};

const ensureCloseFallbackButton = (): HTMLButtonElement => {
    const closeIcon = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" aria-hidden="true"><path d="M18 6L6 18M6 6l12 12"/></svg>';
    let btn = document.getElementById(CHATBASE_CLOSE_FALLBACK_ID) as HTMLButtonElement | null;
    if (btn) {
        if (!btn.querySelector('svg')) {
            btn.innerHTML = closeIcon;
        }
        return btn;
    }

    btn = document.createElement('button');
    btn.id = CHATBASE_CLOSE_FALLBACK_ID;
    btn.type = 'button';
    btn.className = 'tempstars-chatbase-close-btn';
    btn.setAttribute('aria-label', 'Close chat');
    btn.innerHTML = closeIcon;
    btn.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        closeChatbaseWidget();
    });
    document.body.appendChild(btn);
    return btn;
};

const syncCloseFallbackVisibility = () => {
    const chatOpen = getChatbaseSnapshot().status === 'opening' || !!findChatWindow();
    setCloseFallbackVisible(chatOpen);
};

/**
 * Chatbase proactive teaser dismiss uses an HTML entity (×) that renders as a
 * broken "?" box on iOS/Cordova. Replace with an inline SVG on mobile only.
 */
const patchChatbaseTeaserDismissButtons = (): boolean => {
    if (!isMobileChatbase()) return false;

    let patchedAny = false;
    const buttons = document.querySelectorAll<HTMLButtonElement>(
        TEASER_DISMISS_BUTTON_SELECTORS.join(', '),
    );

    buttons.forEach((btn) => {
        if (btn.getAttribute(TEASER_DISMISS_PATCHED_ATTR) === 'true') return;
        btn.setAttribute(TEASER_DISMISS_PATCHED_ATTR, 'true');
        btn.innerHTML = TEASER_DISMISS_SVG;
        btn.setAttribute('aria-label', 'Dismiss message');
        patchedAny = true;
    });

    return patchedAny;
};

let teaserDismissObserver: MutationObserver | null = null;
let teaserDismissPatchTimers: number[] = [];

const stopTeaserDismissPatching = () => {
    teaserDismissPatchTimers.forEach((timer) => window.clearTimeout(timer));
    teaserDismissPatchTimers = [];
    teaserDismissObserver?.disconnect();
    teaserDismissObserver = null;
};

const scheduleTeaserDismissPatching = () => {
    if (!isMobileChatbase()) return;

    patchChatbaseTeaserDismissButtons();
    [400, 1200, 3000, 6000].forEach((delayMs) => {
        teaserDismissPatchTimers.push(
            window.setTimeout(() => {
                if (patchChatbaseTeaserDismissButtons()) {
                    stopTeaserDismissPatching();
                }
            }, delayMs),
        );
    });

    if (teaserDismissObserver) return;

    teaserDismissObserver = new MutationObserver(() => {
        if (patchChatbaseTeaserDismissButtons()) {
            stopTeaserDismissPatching();
        }
    });
    teaserDismissObserver.observe(document.body, { childList: true, subtree: true });
};

const injectChatbaseSafeAreaStyles = () => {
    const css = `
      :root {
        --chatbase-safe-top: max(
          env(safe-area-inset-top, 0px),
          var(--native-safe-area-top, 0px)
        );
      }
      #chatbase-bubble-button,
      [data-chatbase-bubble],
      .chatbase-chat-bubble {
        bottom: calc(env(safe-area-inset-bottom, 0px) + 16px) !important;
      }
      body.${CHATBASE_UI_HIDDEN_CLASS} #chatbase-bubble-button,
      body.${CHATBASE_UI_HIDDEN_CLASS} [data-chatbase-bubble],
      body.${CHATBASE_UI_HIDDEN_CLASS} .chatbase-chat-bubble,
      body.${CHATBASE_UI_HIDDEN_CLASS} #chatbase-message-bubbles,
      body.${CHATBASE_UI_HIDDEN_CLASS} [class*="chatbase-message-bubbles"],
      ${CHAT_WINDOW_SELECTORS.map(selector => `body.${CHATBASE_UI_HIDDEN_CLASS} ${selector}`).join(',\n      ')},
      body.${CHATBASE_UI_HIDDEN_CLASS} .tempstars-chatbase-close-btn {
        display: none !important;
        visibility: hidden !important;
        pointer-events: none !important;
      }
      body.tempstars-chatbase-unavailable #chatbase-bubble-window,
      body.tempstars-chatbase-unavailable #chatbase-chat-window,
      body.tempstars-chatbase-unavailable .tempstars-chatbase-close-btn,
      body.tempstars-chatbase-fallback-visible #chatbase-bubble-button,
      body.tempstars-chatbase-fallback-visible #chatbase-message-bubbles {
        display: none !important;
        visibility: hidden !important;
        pointer-events: none !important;
      }
      body.tempstars-chatbase-inactive #chatbase-bubble-button,
      body.tempstars-chatbase-inactive #chatbase-message-bubbles,
      body.tempstars-chatbase-inactive #chatbase-bubble-window,
      body.tempstars-chatbase-inactive #chatbase-chat-window,
      body.tempstars-chatbase-inactive .tempstars-chatbase-close-btn {
        display: none !important;
        visibility: hidden !important;
        pointer-events: none !important;
      }
      body.tempstars-chatbase-resetting #chatbase-bubble-window iframe,
      body.tempstars-chatbase-resetting #chatbase-chat-window iframe {
        visibility: hidden !important;
      }
      #chatbase-bubble-window,
      .chatbase-bubble-window,
      #chatbase-chat-window,
      .chatbase-chat-window {
        top: calc(var(--chatbase-safe-top) + 8px) !important;
        max-height: calc(100% - var(--chatbase-safe-top) - env(safe-area-inset-bottom, 0px) - 16px) !important;
      }
      /* Proactive teaser dismiss: Chatbase uses × entity which breaks on iOS. */
      @media (pointer: coarse) {
        #chatbase-message-bubbles button[aria-label="Close notifications"],
        #chatbase-message-bubbles button[aria-label="Dismiss message"],
        [class*="chatbase-message-bubbles"] button[aria-label="Close notifications"],
        [class*="chatbase-message-bubbles"] button[aria-label="Dismiss message"] {
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
          width: 28px !important;
          height: 28px !important;
          min-width: 28px !important;
          min-height: 28px !important;
          max-width: 28px !important;
          max-height: 28px !important;
          padding: 0 !important;
          margin: 0 !important;
          border: none !important;
          border-radius: 50% !important;
          background: rgba(17, 24, 39, 0.08) !important;
          color: #374151 !important;
          box-shadow: none !important;
          font-size: 0 !important;
          line-height: 0 !important;
          overflow: hidden !important;
          -webkit-tap-highlight-color: transparent;
        }
        #chatbase-message-bubbles button[aria-label="Close notifications"] svg,
        #chatbase-message-bubbles button[aria-label="Dismiss message"] svg,
        [class*="chatbase-message-bubbles"] button[aria-label="Close notifications"] svg,
        [class*="chatbase-message-bubbles"] button[aria-label="Dismiss message"] svg {
          width: 14px !important;
          height: 14px !important;
          display: block !important;
        }
      }
      .tempstars-chatbase-close-btn {
        position: fixed;
        top: calc(var(--chatbase-safe-top) + 8px);
        right: 16px;
        z-index: 2147483647;
        width: 44px;
        height: 44px;
        border: 1px solid #E5E7EB;
        border-radius: 22px;
        background: #ffffff;
        color: #1F2937;
        display: none;
        align-items: center;
        justify-content: center;
        padding: 0;
        box-shadow: 0 2px 10px rgba(0, 0, 0, 0.18);
        cursor: pointer;
        -webkit-tap-highlight-color: transparent;
        pointer-events: none;
      }
      .tempstars-chatbase-close-btn--visible {
        display: flex;
        pointer-events: auto;
      }
    `;

    const existing = document.getElementById(CHATBASE_STYLE_ID) as HTMLStyleElement | null;
    if (existing) {
        existing.textContent = css;
        return;
    }

    const style = document.createElement('style');
    style.id = CHATBASE_STYLE_ID;
    style.textContent = css;
    document.head.appendChild(style);
};

const ChatbaseWidget: React.FC<ChatbaseWidgetProps> = ({
    disabled = false,
    userContext,
    onLoad,
    onError,
    onStatusChange,
    onContactSupport,
}) => {
    const [state, setState] = useState(getChatbaseSnapshot);
    const isLoaded = state.status !== 'loading' && state.status !== 'unavailable';
    const callbacks = useRef({ onLoad, onError, onStatusChange, onContactSupport });
    callbacks.current = { onLoad, onError, onStatusChange, onContactSupport };
    const userContextRef = useRef(userContext);
    userContextRef.current = userContext;

    useEffect(() => {
        if (disabled || !isFeatureEnabled()) return;
        injectChatbaseSafeAreaStyles();
        ensureCloseFallbackButton();
        const unsubscribe = subscribeChatbase(next => {
            setState(next);
            callbacks.current.onStatusChange?.(next.status);
            if (next.status === 'unavailable') callbacks.current.onError?.(next.error!);
            else if (next.status !== 'loading') callbacks.current.onLoad?.();
        });
        let visibilityTimer: ReturnType<typeof setTimeout>;
        const onOpen = () => {
            clearTimeout(visibilityTimer);
            setCloseFallbackVisible(true);
            visibilityTimer = setTimeout(syncCloseFallbackVisibility, 300);
        };
        const onClose = () => {
            clearTimeout(visibilityTimer);
            setCloseFallbackVisible(false);
        };
        window.addEventListener(CHATBASE_OPEN_EVENT, onOpen);
        window.addEventListener('chatbase:closed', onClose);
        return () => {
            unsubscribe();
            clearTimeout(visibilityTimer);
            window.removeEventListener(CHATBASE_OPEN_EVENT, onOpen);
            window.removeEventListener('chatbase:closed', onClose);
            setCloseFallbackVisible(false);
            stopTeaserDismissPatching();
        };
    }, [disabled]);

    useEffect(() => {
        if (disabled || !isFeatureEnabled()) return;
        let script: HTMLScriptElement | null = null;
        let loadTimer: ReturnType<typeof setTimeout>;
        let availabilityPoll: ReturnType<typeof setInterval>;
        const loaded = () => {
            // Script execution starts an asynchronous vendor initialization.
            // onload can precede the Proxy/API and must not signal failure.
            if (!isChatbaseAvailable()) return;
            clearTimeout(loadTimer);
            clearInterval(availabilityPoll);
            markChatbaseAvailable();
            scheduleTeaserDismissPatching();
        };
        const failed = () => {
            clearTimeout(loadTimer);
            clearInterval(availabilityPoll);
            failChatbase(new Error('Failed to load Chatbase widget script'));
        };
        const initialize = () => {
            if (isChatbaseAvailable()) {
                loaded();
                return;
            }
            const chatbotId = (window as any).TempStars?.Config?.CHATBASE_CHATBOT_ID
                || process.env.REACT_APP_CHATBASE_CHATBOT_ID || 'PRDTKfv5PscPBi3CxLxJW';
            window.embeddedChatbotConfig = { chatbotId, domain: CHATBASE_DOMAIN };
            if (userContextRef.current) {
                window.chatbaseUserConfig = {
                    user_id: userContextRef.current.userId,
                    user_hash: userContextRef.current.userHash,
                    user_metadata: userContextRef.current.metadata,
                };
            }
            const existing = document.querySelector<HTMLScriptElement>(`script[src="${CHATBASE_SCRIPT_SRC}"]`);
            script = existing ?? document.createElement('script');
            script.addEventListener('load', loaded);
            script.addEventListener('error', failed);
            loadTimer = setTimeout(failed, 20000);
            availabilityPoll = setInterval(loaded, 250);
            if (!existing) {
                script.src = CHATBASE_SCRIPT_SRC;
                script.defer = true;
                script.async = true;
                script.setAttribute('data-chatbase', 'true');
                script.setAttribute('chatbotId', chatbotId);
                script.setAttribute('domain', CHATBASE_DOMAIN);
                document.body.appendChild(script);
            }
        };
        // The embed only needs DOM APIs; it can initialize after deviceready has
        // already fired, as happens when navigating from Home to Support.
        const initializeTimer = setTimeout(initialize, isChatbaseAvailable() ? 0 : 1500);
        return () => {
            clearTimeout(initializeTimer);
            clearTimeout(loadTimer);
            clearInterval(availabilityPoll);
            script?.removeEventListener('load', loaded);
            script?.removeEventListener('error', failed);
            // Preserve the SDK between routes; a loaded iframe retains readiness.
        };
    }, [disabled]);

    useEffect(() => {
        if (!disabled && isFeatureEnabled()) setChatbaseActor(userContext?.userId ?? userContext?.metadata?.email);
    }, [disabled, userContext?.userId, userContext?.metadata?.email]);

    useEffect(() => {
        if (disabled || !isLoaded || !userContext) return;
        try {
            window.chatbase?.identify?.({
                user_id: userContext.userId,
                user_hash: userContext.userHash,
                user_metadata: userContext.metadata,
            });
        } catch (error) {
            console.warn('Unable to pass user context to Chatbase:', error);
        }
    }, [disabled, isLoaded, userContext]);

    useEffect(() => {
        window.addEventListener('beforeunload', resetChatbaseConversation);
        return () => window.removeEventListener('beforeunload', resetChatbaseConversation);
    }, []);

    if (disabled || !isFeatureEnabled()) return null;
    return (
        <BrandedAlertModal
            isOpen={state.fallbackVisible}
            onClose={dismissChatbaseFallback}
            headerLabel="Support"
            title="AI chat couldn’t open"
            ariaLabel="AI chat unavailable"
            actions={[{
                label: 'Contact Support',
                ariaLabel: 'Contact Support',
                onClick: () => {
                    dismissChatbaseFallback();
                    callbacks.current.onContactSupport?.();
                },
            }]}
        >
            You can still get help from our team. Open the support form to send your question.
        </BrandedAlertModal>
    );
};

export default ChatbaseWidget;
